import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { CheckPaymentMethodVerification, CreatePaymentMethodWithVerification, MarketplacePaymentInformationState } from '@platform-frontend/marketplace';
import { PollingConfiguration } from '@platform-frontend/platform';
import { InvalidStateError, handleAllErrors, handleSpecificErrors } from '@platform-frontend/util';
import { Observable, throwError } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CheckoutPaymentInformationState } from './checkout-payment-information.state';
import { PaymentMethodPollingConfig } from './model/payment-method-polling-config';

@Injectable({
  providedIn: 'root'
})
export class PaymentMethodPollingService {
  constructor(private store: Store) {}

  getPollingConfiguration(config: PaymentMethodPollingConfig): PollingConfiguration {
    const paymentMethodCreated$ = this.getPaymentMethodCreated();
    return {
      initialize: () => this.initializePolling(config),
      poll: timeout => this.poll(config, timeout).pipe(switchMap(() => paymentMethodCreated$)),
      proceed: () => config.proceed()
    };
  }

  private getPaymentMethodCreated(): Observable<boolean> {
    const initialPaymentMethodCount = this.store.selectSnapshot(CheckoutPaymentInformationState.paymentMethods).length;
    return this.store.selectOnce(CheckoutPaymentInformationState.paymentMethods).pipe(map(paymentMethods => paymentMethods.length > initialPaymentMethodCount));
  }

  private initializePolling(config: PaymentMethodPollingConfig): Observable<unknown> {
    return this.store.dispatch(new CreatePaymentMethodWithVerification('PAY_PAL')).pipe(
      switchMap(() => this.showVerification(config)),
      handleAllErrors(() => config.cancel())
    );
  }

  private showVerification(config: PaymentMethodPollingConfig): Observable<unknown> {
    const redirectData = this.store.selectSnapshot(MarketplacePaymentInformationState.redirection);
    if (redirectData) {
      return config.showVerificationPage(redirectData.url);
    } else {
      return throwError(() => new InvalidStateError('No redirect data available to verify payment method.'));
    }
  }

  private poll(config: PaymentMethodPollingConfig, timeout: number): Observable<unknown> {
    return this.store.dispatch(new CheckPaymentMethodVerification(timeout)).pipe(
      handleSpecificErrors({
        REDIRECT_ID_NOT_KNOWN: () => config.cancel(),
        CANCELED_BY_USER: () => config.cancel()
      })
    );
  }
}
